import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';
import ProfileSectionImage from '../assets/profile-section-Image.png';

interface NutritionalData {
  responses: Array<{
    questionId: string;
    answer: string;
    score: number;
  }>;
  scores: {
    overall: number;
    healthyFoods: number;
    unhealthyFoods: number;
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const NutritionalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [responses, setResponses] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalQuestions = 15; // 15 food items including water intake

  // Phil-HEI + FFQ Questions (Filipino Nutrition Assessment)
  const questions = [
    {
      id: '1',
      question: 'Rice (white rice, brown rice, mixed rice)',
      type: 'frequency'
    },
    {
      id: '2', 
      question: 'Vegetables (leafy greens, gourds, root crops, etc.)',
      type: 'frequency'
    },
    {
      id: '3',
      question: 'Fruits (mango, banana, papaya, citrus, etc.)',
      type: 'frequency'
    },
    {
      id: '4',
      question: 'Fish / seafood (fresh or dried)',
      type: 'frequency'
    },
    {
      id: '5',
      question: 'Poultry and lean meats (chicken, turkey, pork, beef)',
      type: 'frequency'
    },
    {
      id: '6',
      question: 'Legumes (munggo, beans, peanuts)',
      type: 'frequency'
    },
    {
      id: '7',
      question: 'Eggs',
      type: 'frequency'
    },
    {
      id: '8',
      question: 'Milk and dairy products (fresh milk, yogurt, cheese)',
      type: 'frequency'
    },
    {
      id: '9',
      question: 'Cooking oils and fats (palm oil, coconut oil, butter, margarine)',
      type: 'frequency'
    },
    {
      id: '10',
      question: 'Sugary drinks (soft drinks, sweetened juices, powdered drinks)',
      type: 'frequency'
    },
    {
      id: '11',
      question: 'Packaged sweets / desserts (cakes, candies, halo-halo, pastries)',
      type: 'frequency'
    },
    {
      id: '12',
      question: 'Fried foods (deep-fried, fast food items, street foods)',
      type: 'frequency'
    },
    {
      id: '13',
      question: 'Processed meat (hotdog, tocino, longganisa, corned beef)',
      type: 'frequency'
    },
    {
      id: '14',
      question: 'Alcoholic drinks',
      type: 'frequency'
    },
    {
      id: '15',
      question: 'Water intake (glasses per day)',
      type: 'water'
    }
  ];

  const frequencyOptions = [
    { value: '0', label: 'Never / less than once a week', score: 0 },
    { value: '1', label: '1 to 2 times per week', score: 1 },
    { value: '2', label: '3 to 4 times per week', score: 2 },
    { value: '3', label: 'Daily (at least once a day)', score: 3 },
    { value: '4', label: 'More than once per day', score: 4 }
  ];

  const waterOptions = [
    { value: '0', label: 'Less than 4 glasses', score: 0 },
    { value: '1', label: '4-6 glasses', score: 1 },
    { value: '2', label: '7-8 glasses', score: 2 },
    { value: '3', label: '9-10 glasses', score: 3 },
    { value: '4', label: 'More than 10 glasses', score: 4 }
  ];

  const getCurrentQuestionData = () => {
    return questions[currentQuestion - 1];
  };

  const getAnswerOptions = () => {
    const question = getCurrentQuestionData();
    if (!question) {
      return [];
    }
    if (question.type === 'water') {
      return waterOptions;
    }
    return frequencyOptions;
  };

  const saveCurrentResponse = (answer: string) => {
    const question = getCurrentQuestionData();
    if (!question) return;
    
    const questionId = question.id;
    setResponses(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    const question = getCurrentQuestionData();
    if (!question) return;
    
    const questionId = question.id;
    if (!responses[questionId]) return;

    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(prev => prev - 1);
      return;
    }
    // At the first question: always go back to Learn Assessments
    navigate('/panel/learn/');
  };

  const calculateScore = () => {
    let healthyFoodsScore = 0;
    let unhealthyFoodsScore = 0;
    let totalScore = 0;


    // Wellness food groups (questions 1-8, 15): direct scoring
    const wellnessFoodQuestions = ['1', '2', '3', '4', '5', '6', '7', '8', '15'];
    wellnessFoodQuestions.forEach(qId => {
      const answer = responses[qId];
      if (answer !== undefined) {
        const score = parseInt(answer);
        healthyFoodsScore += score;
        totalScore += score;
      }
    });

    // Unhealthy food groups (questions 9-14): reverse scoring
    const unhealthyFoodQuestions = ['9', '10', '11', '12', '13', '14'];
    unhealthyFoodQuestions.forEach(qId => {
      const answer = responses[qId];
      if (answer !== undefined) {
        const score = parseInt(answer);
        const reverseScore = 4 - score; // Reverse scoring
        unhealthyFoodsScore += reverseScore;
        totalScore += reverseScore;
      }
    });

    // Scale to 0-100 (from 0-60 range)
    const scaledScore = Math.round((totalScore / 60) * 100);

    const result = {
      overall: scaledScore,
      healthyFoods: Math.round((healthyFoodsScore / 36) * 100), // 9 items * 4 max
      unhealthyFoods: Math.round((unhealthyFoodsScore / 24) * 100) // 6 items * 4 max
    };

    return result;
  };

  const getDietQualityLevel = (score: number) => {
    if (score >= 75) return 'High';
    if (score >= 50) return 'Moderate';
    return 'Low';
  };

  const getInterpretation = (level: string, score: number) => {
    switch (level) {
      case 'High':
        return 'Excellent diet quality! You consistently follow Philippine Dietary Guidelines with a balanced and nutrient-dense diet.';
      case 'Moderate':
        return 'Good diet quality with some healthy eating patterns, but there are opportunities to improve specific food groups.';
      case 'Low':
        return 'Your diet quality needs improvement. Focus on increasing fruits, vegetables, and whole foods while reducing processed items.';
      default:
        return 'Complete the assessment to get your personalized nutrition insights.';
    }
  };

  const getRecommendations = (responses: {[key: string]: string}) => {
    const recommendations = [];

    // Check vegetable intake
    if (parseInt(responses['2'] || '0') < 2) {
      recommendations.push('Increase vegetable intake - aim for at least 3-4 times per week');
    }

    // Check fruit intake
    if (parseInt(responses['3'] || '0') < 2) {
      recommendations.push('Eat more fruits - target daily consumption of local fruits');
    }

    // Check water intake
    if (parseInt(responses['15'] || '0') < 2) {
      recommendations.push('Increase water intake - aim for 7-8 glasses daily');
    }

    // Check sugary drinks
    if (parseInt(responses['10'] || '0') > 1) {
      recommendations.push('Reduce sugary drinks - limit soft drinks and sweetened beverages');
    }

    // Check fried foods
    if (parseInt(responses['12'] || '0') > 2) {
      recommendations.push('Limit fried foods - choose grilled or steamed options more often');
    }

    // Check processed meats
    if (parseInt(responses['13'] || '0') > 1) {
      recommendations.push('Reduce processed meat consumption - choose fresh fish and lean meats');
    }

    if (recommendations.length === 0) {
      recommendations.push('Maintain your current healthy eating patterns!');
    }

    return recommendations;
  };

  const handleSubmit = async () => {
    if (currentQuestion !== totalQuestions) return;

    const question = getCurrentQuestionData();
    if (!question) return;
    
    const questionId = question.id;
    if (!responses[questionId]) return;

    setIsSubmitting(true);

    try {
      
      const scores = calculateScore();
      const level = getDietQualityLevel(scores.overall);
      const interpretation = getInterpretation(level, scores.overall);
      const recommendations = getRecommendations(responses);
      

      const nutritionalData: NutritionalData = {
        responses: Object.entries(responses).map(([questionId, answer]) => ({
          questionId,
          answer,
          score: parseInt(answer)
        })),
        scores,
        results: {
          level: level as 'Low' | 'Moderate' | 'High',
          interpretation,
          recommendations
        }
      };

      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_CONFIG.ENDPOINTS.NUTRITIONAL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          assessmentType: 'phil-hei',
          responses: nutritionalData.responses,
          scores: nutritionalData.scores,
          results: nutritionalData.results
        }),
      });
      

      if (response.ok) {
        const result = await response.json();
        // Notify InsightsPage about the update
        assessmentUpdateNotifier.notifyUpdate('nutritional');
        // Navigate to completion page with assessment result
        navigate('/panel/learn/nutritional-assessment/completion', {
          state: { assessmentResult: result.data }
        });
      } else {
        const errorData = await response.text();

        if (response.status === 401) {
          alert('Your session has expired. Please log in again to continue.');
        } else {
          alert('Failed to submit assessment. Please try again.');
        }
      }
    } catch (error) {

      let errorMessage = 'Network error. Please check your connection and try again.';
      if (error instanceof Error && error.message.includes('401')) {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestionData = getCurrentQuestionData();
  const answerOptions = getAnswerOptions();
  const progress = (currentQuestion / totalQuestions) * 100;

  // Early return if no current question data
  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel nutritional-panel">
            <div className="assessment-header">
              <h1>Nutritional Assessment</h1>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        <div className="assessment-left-panel nutritional-panel">
          <div className="assessment-header">
            <h1>Nutritional Assessment</h1>
            <p>Answer honestly to get the most accurate results</p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            <h2 className="question-title">
              {currentQuestionData.question}
            </h2>
            <p className="question-instruction">
              For the past 7 days, how often did you consume this food item?
            </p>

            <div className="answer-options">
              {answerOptions.map((option) => (
                <label key={option.value} className="answer-option">
                  <input
                    type="radio"
                    name={`question-${currentQuestion}`}
                    value={option.value}
                    checked={responses[currentQuestionData.id] === option.value}
                    onChange={(e) => saveCurrentResponse(e.target.value)}
                  />
                  <span className="option-text">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              Back
            </button>
            
            {currentQuestion < totalQuestions ? (
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!responses[currentQuestionData.id]}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={!responses[currentQuestionData.id] || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Complete Assessment'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalAssessmentFormPage;



